import { useState, useEffect } from 'react';

import useToggle from './useToggle';

/**
 * Used to expand and collapse a container while maintaining a consistent vertical page offset.
 *
 * @param {MutableRefObject} containerRef the ref prop of the expandable list
 * @param {boolean=false} controlledIsExpanded pass true if the expandable list is always expanded
 * @returns {(isExpanded, toggleIsExpanded)[]} is container expanded, function to call when container height changes
 */
function useSmoothCollapse({ containerRef, controlledIsExpanded = false }) {
    if (!containerRef) {
        throw new ReferenceError('useSmoothCollapse requires a containerRef for the wrapping container',
            'useSmoothCollapse.js');
    }

    const [ isExpanded, toggleExpanded ] = useToggle(controlledIsExpanded);
    const [ collapsedContainerHeight, setCollapsedContainerHeight ] = useState();

    useEffect(() => {
        if (controlledIsExpanded !== isExpanded) {
            // prevent isExpanded from becoming stale and prevent page jank
            toggleIsExpanded();
        }
    }, [ controlledIsExpanded ]);

    function toggleIsExpanded() {
        if (controlledIsExpanded) {
            return;
        }

        /* Requirement: When collapsing the list, we want to scroll up by the same amount that
            the container was reduced, so the rest of the page content doesn’t appear to jank.
            We’re storing the collapsed height of the container and subtracting it by the expanded
            height to scroll the window by the negative difference (in an upwards direction) */
        const currentContainerHeight = containerRef?.current?.offsetHeight;

        if (isExpanded && collapsedContainerHeight && currentContainerHeight) {
            window?.scrollBy({ top: collapsedContainerHeight - currentContainerHeight });
        } else if (!isExpanded) {
            setCollapsedContainerHeight(currentContainerHeight);
        }

        toggleExpanded();
    }

    return [
        isExpanded,
        toggleIsExpanded,
    ];
}


export default useSmoothCollapse;
